import React, { useEffect, useMemo, useState } from "react"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { FormikHelpers, useFormik } from "formik"
import * as Yup from "yup"
import DataTableTemplate from "Components/templates/data-table-template"
import Modal from "Components/molecule/modal"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import ActionButton from "Components/atoms/buttons/action-button"
import IconButton from "Components/atoms/buttons/icon-button"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import TextInput from "Components/molecule/inputs/text-input"
import MultiSelect from "Components/molecule/inputs/multi-select"
import SearchSelect from "Components/molecule/inputs/search-select"
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container"
import { PlusIcon, PencilIcon, TrashIcon } from "Components/atoms/icons"
import { ClientMapping, ClientMappingFormFields } from "models/client-mapping"
import {
    AdminClientMappingSliceState, addMapping, removeMapping, setEditingMapping, setMapping, setMappings,
} from "slices/admin/client-mapping/reducer"
import {
    deleteClientMappingRequest, getClientMappingFormFields, getClientMappings, submitCreateClientMapping, submitEditClientMapping,
} from "slices/admin/client-mapping/thunk"
import FiscalCalendar from "utils/fiscal-calendar"
import { formatCurrency, formatDateTimeAmPm } from "utils/tools"

const selectMappings = createSelector(
    (state: any) => state.AdminClientMapping,
    ({ mappings, editingMapping }: AdminClientMappingSliceState) => ({ mappings, editingMapping })
)

interface TargetField {
    fiscalYear: number | ''
    annualRevenue: number | ''
}

interface Fields {
    name: string
    ownerId: string
    clientIds: string[]
    targets: TargetField[]
}

const EMPTY_FORM: Fields = { name: '', ownerId: '', clientIds: [], targets: [] }

const Validation = Yup.object({
    name: Yup.string().trim().required("Name is required").max(150, "150 characters max"),
    ownerId: Yup.string(),
    clientIds: Yup.array().of(Yup.string()),
    targets: Yup.array().of(Yup.object({
        fiscalYear: Yup.number().typeError("Required").required("Required").integer("Whole number")
            .min(2000, "Too early").max(2100, "Too late"),
        annualRevenue: Yup.number().typeError("Required").required("Required").min(0, "0 or more").max(100_000_000, "Too large"),
    })).test("unique-years", "Each fiscal year can have only one target",
        (targets) => !targets || new Set(targets.map(t => t.fiscalYear)).size === targets.length),
})

const currentFY = FiscalCalendar.currentFiscalYear()

const ClientMappingPage = () => {
    document.title = "Client Mapping | IbexIQ";
    const dispatch: any = useDispatch()
    const { mappings, editingMapping } = useSelector(selectMappings)

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [formFields, setFormFields] = useState<ClientMappingFormFields>({ customers: [], owners: [] })
    const [showFormModal, setShowFormModal] = useState<boolean>(false)
    const [focusedMapping, setFocusedMapping] = useState<ClientMapping | null>(null)
    const [isDeleting, setIsDeleting] = useState<boolean>(false)

    const loadFormFields = async () => {
        const res = await getClientMappingFormFields()
        if (res) setFormFields(res)
    }

    useEffect(() => {
        const init = async () => {
            const [res] = await Promise.all([getClientMappings(), loadFormFields()])
            if (res) dispatch(setMappings(res))
            setIsLoading(false)
        }
        init()
    }, [])

    const handleCloseFormModal = () => {
        setShowFormModal(false)
        dispatch(setEditingMapping(null))
        resetForm()
    }

    const onSubmit = async (vals: Fields, helpers: FormikHelpers<Fields>) => {
        helpers.setSubmitting(true)
        const payload = {
            name: vals.name.trim(),
            ownerId: vals.ownerId || null,
            clientIds: vals.clientIds,
            targets: vals.targets.map(t => ({ fiscalYear: Number(t.fiscalYear), annualRevenue: Number(t.annualRevenue) })),
        }

        const res = editingMapping
            ? await submitEditClientMapping(editingMapping.id, payload)
            : await submitCreateClientMapping(payload)

        if (res) {
            dispatch(editingMapping ? setMapping(res) : addMapping(res))
            await loadFormFields()
            handleCloseFormModal()
        }
        helpers.setSubmitting(false)
    }

    const { values, errors, touched, handleBlur, handleSubmit, setValues, setFieldValue, setFieldTouched, resetForm, isSubmitting, isValid } =
        useFormik<Fields>({ initialValues: EMPTY_FORM, onSubmit, validationSchema: Validation })

    // Customers that are free or already in the client being edited
    const customerOptions = useMemo(() => formFields.customers
        .filter(c => !c.clientMappingId || c.clientMappingId === editingMapping?.id)
        .map(c => ({ value: c.id, label: c.name })), [formFields.customers, editingMapping])

    const ownerOptions = useMemo(() => formFields.owners.map(o => ({ value: o.id, label: o.name })), [formFields.owners])

    const handleAdd = () => {
        dispatch(setEditingMapping(null))
        setValues({ ...EMPTY_FORM, targets: [{ fiscalYear: currentFY, annualRevenue: '' }] }, false)
        setShowFormModal(true)
    }

    const handleEdit = (mapping: ClientMapping) => {
        dispatch(setEditingMapping(mapping))
        setValues({
            name: mapping.name,
            ownerId: mapping.owner?.id ?? '',
            clientIds: mapping.customers.map(c => c.id),
            targets: mapping.targets.map(t => ({ fiscalYear: t.fiscalYear, annualRevenue: t.annualRevenue })),
        }, false)
        setShowFormModal(true)
    }

    const addTargetRow = () => {
        const years = values.targets.map(t => Number(t.fiscalYear)).filter(Boolean)
        const next = years.length ? Math.max(...years) + 1 : currentFY
        setFieldValue("targets", [...values.targets, { fiscalYear: next, annualRevenue: '' }])
    }

    const setTargetValue = (index: number, key: keyof TargetField, raw: string) => {
        const next = [...values.targets]
        next[index] = { ...next[index], [key]: raw === '' ? '' : Number(raw) }
        setFieldValue("targets", next)
    }

    const removeTargetRow = (index: number) =>
        setFieldValue("targets", values.targets.filter((_, i) => i !== index))

    const targetError = (index: number, key: keyof TargetField): string | undefined => {
        const e = (errors.targets as any)?.[index]?.[key]
        return touched.targets ? e : undefined
    }

    const handleCloseDeleteModal = async () => setFocusedMapping(null)

    const confirmDelete = async () => {
        if (!focusedMapping) return
        setIsDeleting(true)
        if (await deleteClientMappingRequest(focusedMapping.id)) {
            dispatch(removeMapping(focusedMapping.id))
            await loadFormFields()
            setFocusedMapping(null)
        }
        setIsDeleting(false)
    }

    const columns = React.useMemo<ColumnDef<ClientMapping>[]>(() => [
        { header: "Client", accessorKey: "name", size: 220, cell: ({ row }) => <strong>{row.original.name}</strong> },
        { header: "Owner", id: "owner", size: 180, accessorFn: (r) => r.owner?.name ?? "", cell: ({ row }) => row.original.owner?.name ?? <span className="text-muted">-</span> },
        {
            header: "Acumatica Customers",
            id: "customers",
            size: 340,
            accessorFn: (r) => r.customers.map(c => c.name).join(", "),
            cell: ({ row }) => row.original.customers.length
                ? <span title={row.original.customers.map(c => c.name).join("\n")}>{row.original.customers.map(c => c.name).join(", ")}</span>
                : <span className="text-muted">No customers</span>,
        },
        {
            header: `FY${currentFY} Target`,
            id: "target",
            size: 150,
            accessorFn: (r) => r.targets.find(t => t.fiscalYear === currentFY)?.annualRevenue,
            cell: ({ row }) => {
                const target = row.original.targets.find(t => t.fiscalYear === currentFY)
                return target ? formatCurrency(target.annualRevenue) : <span className="text-muted">-</span>
            },
        },
        {
            header: "Updated",
            id: "updated",
            size: 260,
            accessorFn: (r) => r.updatedAt ? new Date(r.updatedAt).getTime() : undefined,
            cell: ({ row }) => <TableRowControlsContainer style={{ justifyContent: 'space-between' }}>
                <span>
                    {row.original.updatedBy ?? "-"}
                    <br /><small className="text-muted">{formatDateTimeAmPm(row.original.updatedAt)}</small>
                </span>
                <div className='d-flex flex-row justify-content-end'>
                    <IconButton onClick={() => handleEdit(row.original)}><PencilIcon /></IconButton>
                    <IconButton onClick={() => setFocusedMapping(row.original)}><TrashIcon /></IconButton>
                </div>
            </TableRowControlsContainer>
        },
    ], [])

    const table = useDataTable({
        columns,
        data: mappings,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        title="Admin"
        breadcrumbItem="Client Mapping"
        table={table}
        columns={columns}
        isLoading={isLoading}
        actions={[
            <ActionButton key="add" onClick={handleAdd}>Add <PlusIcon /></ActionButton>
        ]}
        modal={<>
            <Modal
                isOpen={showFormModal}
                onClose={handleCloseFormModal}
                title={editingMapping ? `Edit ${editingMapping.name}` : "Create Client"}
            >
                <div className="generate-documents-form">
                    <TextInput
                        name="name"
                        label="Client Name*"
                        placeholder="e.g. FortisBC (Gas)"
                        touched={!!touched.name}
                        value={values.name}
                        onBlur={handleBlur}
                        onChange={(e) => setFieldValue("name", e.target.value)}
                        error={!!touched.name && !!errors.name}
                        message={touched.name ? errors.name : undefined}
                    />
                    <SearchSelect
                        name="ownerId"
                        label="Owner"
                        placeholder="Select owner"
                        options={ownerOptions}
                        value={values.ownerId}
                        onChange={(opt) => setFieldValue("ownerId", opt?.value ?? '')}
                        clearable
                        menuPortal
                    />
                    <MultiSelect
                        name="clientIds"
                        label="Acumatica Customers"
                        placeholder="Select customers"
                        options={customerOptions}
                        value={customerOptions.filter(o => values.clientIds.includes(o.value))}
                        onChange={(opts) => setFieldValue("clientIds", opts.map(o => o.value))}
                        menuPortal
                    />
                    <small className="text-muted d-block mb-3">
                        Only customers with projects are listed. A customer can belong to one client only; customers already mapped elsewhere are hidden.
                    </small>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="input-label">Annual Revenue Targets</span>
                        <SecondaryButton type="button" onClick={addTargetRow}>Add year <PlusIcon /></SecondaryButton>
                    </div>
                    {values.targets.length === 0 && <small className="text-muted d-block mb-3">No targets yet.</small>}
                    {values.targets.map((t, i) => (
                        <div key={i} className="d-flex gap-2 align-items-start">
                            <TextInput
                                type="number"
                                name={`targets.${i}.fiscalYear`}
                                label="Fiscal Year*"
                                value={t.fiscalYear}
                                containerStyle={{ width: 130 }}
                                onBlur={() => setFieldTouched("targets", true, false)}
                                onChange={(e) => setTargetValue(i, "fiscalYear", e.target.value)}
                                error={!!targetError(i, "fiscalYear")}
                                message={targetError(i, "fiscalYear")}
                            />
                            <TextInput
                                type="number"
                                name={`targets.${i}.annualRevenue`}
                                label="Annual Revenue ($)*"
                                placeholder="e.g. 1474000"
                                value={t.annualRevenue}
                                containerStyle={{ flex: 1 }}
                                onBlur={() => setFieldTouched("targets", true, false)}
                                onChange={(e) => setTargetValue(i, "annualRevenue", e.target.value)}
                                error={!!targetError(i, "annualRevenue")}
                                message={targetError(i, "annualRevenue")}
                            />
                            <div style={{ paddingTop: 26 }}>
                                <IconButton onClick={() => removeTargetRow(i)}><TrashIcon /></IconButton>
                            </div>
                        </div>
                    ))}
                    {typeof errors.targets === "string" && <small className="text-danger d-block mb-2">{errors.targets}</small>}

                    <PrimaryButton
                        type='submit'
                        onClick={() => handleSubmit()}
                        disabled={isSubmitting || !isValid}
                        className="w-100 mt-2"
                    >
                        {isSubmitting ? "Saving..." : editingMapping ? "Update" : "Create"}
                    </PrimaryButton>
                </div>
            </Modal>
            <AreYouSureModal
                isOpen={!!focusedMapping}
                onModalClose={handleCloseDeleteModal}
                modalTitle="Delete Client"
                subTitle={`Delete ${focusedMapping?.name ?? ""}? Its customer mapping and targets are deleted too.`}
                handleAreYouSureAccept={confirmDelete}
                handleAreYouSureDecline={handleCloseDeleteModal}
                areYouSureacceptText={isDeleting ? 'Deleting...' : 'Delete'}
                areYouSuredeclineText="Cancel"
            />
        </>}
    />
}

export default ClientMappingPage
